import React, { useState, useRef, useEffect } from 'react';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';
import { ChevronDown, Check, Globe } from 'lucide-react';

const LanguageSelector = ({ variant = 'default' }) => {
  const { language, setLanguage, getCurrentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = getCurrentLanguage();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  // Group languages
  const indianLanguages = SUPPORTED_LANGUAGES.filter(l => 
    ['hi', 'mr', 'ta', 'te', 'bn', 'gu', 'kn', 'ml', 'pa', 'or', 'as'].includes(l.code)
  );
  const internationalLanguages = SUPPORTED_LANGUAGES.filter(l => 
    ['en', 'es', 'fr', 'de', 'ru', 'it', 'zh', 'ja'].includes(l.code)
  );
  const rtlLanguages = SUPPORTED_LANGUAGES.filter(l => l.rtl);

  if (variant === 'minimal') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1 text-sm text-gray-300 hover:text-white transition-colors"
          data-testid="language-selector-minimal"
        >
          <Globe className="w-4 h-4" />
          <span>{currentLang.code.toUpperCase()}</span>
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-800 transition-colors ${
                  language === lang.code ? 'bg-amber-500/10 text-amber-500' : 'text-gray-300'
                }`}
              >
                <span>{lang.flag}</span>
                <span>{lang.nativeName}</span>
                {language === lang.code && <Check className="w-4 h-4 ml-auto" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white hover:bg-gray-800 transition-colors"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4 text-amber-500" />
        <span className="text-lg">{currentLang.flag}</span>
        <span className="hidden sm:inline">{currentLang.nativeName}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Indian Languages */}
          <div className="p-2 border-b border-gray-800">
            <p className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
              Indian Languages
            </p>
            <div className="grid grid-cols-2 gap-1">
              {indianLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className={`px-3 py-2 text-left text-sm flex items-center gap-2 rounded-lg hover:bg-gray-800 transition-colors ${
                    language === lang.code ? 'bg-amber-500/10 text-amber-500' : 'text-gray-300'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span className="truncate">{lang.nativeName}</span>
                  {language === lang.code && <Check className="w-3 h-3 ml-auto flex-shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          {/* International Languages */}
          <div className="p-2 border-b border-gray-800">
            <p className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
              International
            </p>
            <div className="grid grid-cols-2 gap-1">
              {internationalLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className={`px-3 py-2 text-left text-sm flex items-center gap-2 rounded-lg hover:bg-gray-800 transition-colors ${
                    language === lang.code ? 'bg-amber-500/10 text-amber-500' : 'text-gray-300'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span className="truncate">{lang.nativeName}</span>
                  {language === lang.code && <Check className="w-3 h-3 ml-auto flex-shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          {/* RTL Languages */}
          <div className="p-2">
            <p className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
              RTL Languages
            </p>
            <div className="grid grid-cols-2 gap-1">
              {rtlLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  dir="rtl"
                  className={`px-3 py-2 text-right text-sm flex items-center gap-2 rounded-lg hover:bg-gray-800 transition-colors ${
                    language === lang.code ? 'bg-amber-500/10 text-amber-500' : 'text-gray-300'
                  }`}
                >
                  {language === lang.code && <Check className="w-3 h-3 mr-auto flex-shrink-0" />}
                  <span className="truncate">{lang.nativeName}</span>
                  <span>{lang.flag}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
